import { apiGet } from "./client";

export type DeskGlanceQueueMetric = {
  count: number;
  outstanding?: string;
};

export type DeskGlanceSalesToday = {
  /** Tax-incl recognized gross for company-local today — plan/44 */
  amount: string;
  /** Same formula for yesterday (expand vs-period). */
  yesterdayAmount: string;
  /** Oldest → newest, 7 company-local days ending today (sparkline). */
  last7: string[];
  /** Oldest → newest, 30 company-local days ending today — plan/50. */
  last30: string[];
  /** Oldest → newest, month-start → today — plan/50. */
  lastMtd: string[];
};

export type DeskGlanceLowStock = {
  count: number;
  threshold: string;
};

export type DeskGlanceAgingBuckets = {
  current: string;
  d1_30: string;
  d31_60: string;
  d61_90: string;
  d90_plus: string;
};

export type DeskGlanceTopProduct = {
  productId: string;
  name: string;
  sku: string | null;
  amount: string;
  qty: string;
};

export type DeskGlanceTopParty = {
  partyId: string;
  name: string;
  balance: string;
};

export type DeskGlanceTopLowStock = {
  productId: string;
  name: string;
  sku: string | null;
  onHand: string;
  threshold: string;
};

export type DeskGlance = {
  salesToday: DeskGlanceSalesToday | null;
  toCollect: DeskGlanceQueueMetric | null;
  /** Open SO AR aging — plan/50 home widget. */
  toCollectAging?: DeskGlanceAgingBuckets | null;
  toDeliver: DeskGlanceQueueMetric | null;
  toPay: DeskGlanceQueueMetric | null;
  /** Open PO AP aging by payment due — plan/50. */
  toPayAging?: DeskGlanceAgingBuckets | null;
  toReceive: DeskGlanceQueueMetric | null;
  lowStock: DeskGlanceLowStock | null;
  /** Last-7 Top products — plan/50. */
  topProducts?: DeskGlanceTopProduct[] | null;
  /** Last-30 Top products — plan/50. */
  topProducts30?: DeskGlanceTopProduct[] | null;
  /** Month-to-date Top products — plan/50. */
  topProductsMtd?: DeskGlanceTopProduct[] | null;
  /** Open AR Top parties — plan/50. */
  topToCollect?: DeskGlanceTopParty[] | null;
  /** Top low-stock vs effective threshold — plan/50. */
  topLowStock?: DeskGlanceTopLowStock[] | null;
};

/** Owner glance aggregates — plan/44 */
export function fetchDeskGlance(companyRef: string, signal?: AbortSignal) {
  return apiGet<DeskGlance>(`/c/${companyRef}/desk/glance`, { signal });
}

/** Custom home Sales trend series — plan/50. */
export function fetchDeskSalesSeries(
  companyRef: string,
  from: string,
  to: string,
  signal?: AbortSignal,
) {
  const q = new URLSearchParams({ from, to });
  return apiGet<{ from: string; to: string; series: string[] }>(
    `/c/${companyRef}/desk/glance/sales-series?${q}`,
    { signal },
  );
}

/** Custom home Top products — plan/50. */
export function fetchDeskTopProductsRange(
  companyRef: string,
  from: string,
  to: string,
  signal?: AbortSignal,
) {
  const q = new URLSearchParams({ from, to });
  return apiGet<{
    from: string;
    to: string;
    rows: DeskGlanceTopProduct[];
  }>(`/c/${companyRef}/desk/glance/top-products?${q}`, { signal });
}
